import { useEffect, useRef } from "react";
import { App as CapApp, URLOpenListenerEvent } from "@capacitor/app";

import { Capacitor } from "@capacitor/core";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useDeepLinkContext } from "@/contexts/DeepLinkContext";
import { deepLinkService } from "@/services/deepLink";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Mutexes to prevent concurrent analysis runs
let isAnalyzingImages = false;
let isExtractingUrls = false;

// Track whether the cold-launch URL has already been processed.
// CapApp.getLaunchUrl() returns the same URL on every call, so
// we must only handle it once to avoid re-navigating on effect re-runs.
let launchUrlProcessed = false;

// Deep link dedup: track last processed URL + timestamp to prevent
// stale or duplicate deep links from navigating to old items.
let lastProcessedDeepLink: { url: string; timestamp: number } | null = null;
const DEEP_LINK_DEDUP_WINDOW_MS = 10_000; // 10 seconds

/**
 * Fetch and analyze un-processed image items (e.g., saved from share extension).
 * Runs fire-and-forget: fetches image → converts to base64 → calls analyze-image → updates item.
 * Uses a mutex to prevent concurrent runs.
 */
async function analyzeUnprocessedImageItems(
  userId: string,
  queryClient: ReturnType<typeof useQueryClient>,
) {
  // Prevent concurrent runs
  if (isAnalyzingImages) {
    console.log("[App] Image analysis already in progress, skipping");
    return;
  }

  isAnalyzingImages = true;

  try {
    const { data: itemsRaw, error } = await supabase
      .from("saved_items")
      .select(
        "id, image_url, page_id, flux_pages:page_id(data)",
      )
      .eq("user_id", userId)
      .eq("source_type", "image")
      .eq("ai_analyzed", false);

    if (error || !itemsRaw?.length) return;

    // Resolve image_url from flux_pages → saved_items
    const items = itemsRaw
      .map((row: any) => {
        const fp = row.flux_pages;
        const d = fp?.data;
        const imageUrl =
          row.page_id && d && typeof d === "object"
            ? d.cdnUrls?.[0] || d.imageUrls?.[0] || row.image_url || null
            : (row.image_url ?? null);
        return { id: row.id, image_url: imageUrl };
      })
      .filter((item: any) => item.image_url);

    if (!items.length) return;

    console.log(
      `[App] Found ${items.length} un-analyzed image item(s), processing...`,
    );

    for (const item of items) {
      try {
        // Fetch the image and convert to base64 data URL
        const response = await fetch(item.image_url!);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }
        const blob = await response.blob();
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });

        // Call the analyze-image edge function
        const { data, error: analyzeError } = await supabase.functions.invoke(
          "analyze-image",
          {
            body: { imageBase64: base64 },
          },
        );

        if (!analyzeError && data?.success && data?.data) {
          const analysis = data.data;
          // AI + product data goes to saved_items directly (image items have no page_id)
          await supabase
            .from("saved_items")
            .update({
              name: analysis.title,
              note: analysis.description,
              ai_analyzed: true,
              ai_summary: analysis.description,
              ai_category: analysis.category,
              ai_products: analysis.products || [],
              ai_tags: analysis.style?.tags || [],
              ai_person: analysis.person || null,
              ai_focal_point: analysis.focalPoint || null,
            } as any)
            .eq("id", item.id);
        } else {
          // Mark as analyzed to prevent perpetual retries
          await supabase
            .from("saved_items")
            .update({ ai_analyzed: true, name: "Saved Image" } as any)
            .eq("id", item.id);
        }

        // Refresh the UI
        queryClient.invalidateQueries({ queryKey: ["saved-items"] });
      } catch (e) {
        console.error(`[App] Failed to analyze image item ${item.id}:`, e);
        // Mark as analyzed to prevent perpetual retries
        try {
          await supabase
            .from("saved_items")
            .update({ ai_analyzed: true, name: "Saved Image" } as any)
            .eq("id", item.id);
        } catch {}
      }
    }
  } catch (e) {
    console.error("[App] Failed to check for un-analyzed image items:", e);
  } finally {
    // Release mutex
    isAnalyzingImages = false;
  }
}

/**
 * Extract product details for URL items saved from the share extension.
 * These items are saved as skeletons (name=domain, no image/price).
 * Calls extract-url edge function and updates the item with full details.
 */
async function extractUnprocessedUrlItems(
  userId: string,
  queryClient: ReturnType<typeof useQueryClient>,
) {
  if (isExtractingUrls) {
    console.log("[App] URL extraction already in progress, skipping");
    return;
  }

  isExtractingUrls = true;

  try {
    const { data: itemsRaw, error } = await supabase
      .from("saved_items")
      .select("id, url, page_id, flux_pages:page_id(url)")
      .eq("user_id", userId)
      .eq("source_type", "url")
      .eq("ai_analyzed", false);

    // Resolve URL from flux_pages → saved_items
    const items = (itemsRaw || [])
      .map((row: any) => {
        const fpUrl = row.flux_pages?.url;
        return {
          id: row.id,
          page_id: row.page_id,
          url: fpUrl || row.url || null,
        };
      })
      .filter((item: any) => item.url);

    if (error || !items?.length) return;

    console.log(
      `[App] Found ${items.length} un-extracted URL item(s), processing...`,
    );

    for (const item of items) {
      try {
        const { data, error: extractError } = await supabase.functions.invoke(
          "extract-url",
          {
            body: { url: item.url },
          },
        );

        if (!extractError && data?.success && data?.data) {
          const extracted = data.data;
          const pageId = extracted.fluxPageId || item.page_id;

          // Dedup check: if page_ai_data already exists, skip AI write
          let skipAiWrite = false;
          if (pageId) {
            const { data: existing } = await supabase
              .from("page_ai_data")
              .select("page_id")
              .eq("page_id", pageId)
              .maybeSingle();
            if (existing) skipAiWrite = true;
          }

          // Parse price (same logic as AddItemSheet)
          let price: number | undefined;
          if (extracted.price) {
            const numbers = extracted.price.match(/[\d,]+(?:\.\d+)?/g);
            if (numbers) {
              const parsed = numbers.map((n: string) =>
                parseFloat(n.replace(/,/g, "")),
              );
              price = Math.min(...parsed.filter((n: number) => n > 0));
            }
          }

          let originalPrice: number | undefined;
          if (extracted.originalPrice) {
            const numbers = extracted.originalPrice.match(/[\d,]+(?:\.\d+)?/g);
            if (numbers) {
              const parsed = numbers.map((n: string) =>
                parseFloat(n.replace(/,/g, "")),
              );
              originalPrice = Math.min(...parsed.filter((n: number) => n > 0));
            }
          }

          // AI data goes to page_ai_data (URL items with page_id)
          if (!skipAiWrite && pageId) {
            await supabase.from("page_ai_data").upsert(
              {
                page_id: pageId,
                ai_summary: extracted.description,
                ai_category: extracted.category,
                ai_products: extracted.products || [],
                ai_tags: extracted.tags || [],
                ai_person: extracted.person || null,
                ai_focal_point: extracted.focalPoint || null,
                platform: extracted.platform,
                creator_name: extracted.creatorName || null,
                creator_bio_link: extracted.creatorBioLink || null,
              } as any,
              { onConflict: "page_id" },
            );
          }

          // Update saved_items with core + product fields
          const coreUpdate: any = {
            note: extracted.description,
            ai_analyzed: true,
            name: extracted.title,
            image_url: extracted.image || null,
            image_urls: extracted.images || [],
            price,
            original_price: originalPrice,
            store: extracted.platform || null,
            rating: extracted.rating || null,
            review_count: extracted.reviewCount || null,
          };
          if (extracted.fluxPageId) coreUpdate.page_id = extracted.fluxPageId;
          await supabase
            .from("saved_items")
            .update(coreUpdate)
            .eq("id", item.id);
        } else {
          // Mark as analyzed to prevent perpetual retries
          await supabase
            .from("saved_items")
            .update({ ai_analyzed: true })
            .eq("id", item.id);
        }

        queryClient.invalidateQueries({ queryKey: ["saved-items"] });
      } catch (e) {
        console.error(`[App] Failed to extract URL item ${item.id}:`, e);
        try {
          await supabase
            .from("saved_items")
            .update({ ai_analyzed: true })
            .eq("id", item.id);
        } catch {}
      }
    }
  } catch (e) {
    console.error("[App] Failed to check for un-extracted URL items:", e);
  } finally {
    isExtractingUrls = false;
  }
}

/**
 * App Configuration Hook
 *
 * Handles:
 * 1. Deep link handling (want://... URLs) with auth gating and validation
 * 2. Universal Links (https://wntd.ai/... URLs)
 * 3. App state changes (background/foreground)
 * 4. Back button handling on Android
 *
 * Deep link format examples:
 * - want://saved/123?ref=CODE
 * - want://collection/456
 * - want://user/789
 * - want://vote?poll=123
 * - want://try-on?look=456
 *
 * Universal link examples:
 * - https://wntd.ai/saved/123
 * - https://wntd.ai/collection/456
 *
 * Usage: Call once in root component (App.tsx)
 */
export const useAppConfig = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();
  const { pendingUrl, setPendingUrl } = useDeepLinkContext();

  // Use refs so the single-run effect always reads the latest values
  // without needing to re-run (which would tear down & recreate listeners).
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  const userRef = useRef(user);
  const authLoadingRef = useRef(authLoading);
  const setPendingUrlRef = useRef(setPendingUrl);
  const queryClientRef = useRef(queryClient);

  // Keep refs in sync with current values
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    authLoadingRef.current = authLoading;
  }, [authLoading]);
  useEffect(() => {
    setPendingUrlRef.current = setPendingUrl;
  }, [setPendingUrl]);
  useEffect(() => {
    queryClientRef.current = queryClient;
  }, [queryClient]);

  // Single-run effect: sets up all Capacitor listeners ONCE and never tears them down
  // until the component unmounts. This prevents Capacitor from re-delivering URLs
  // when listeners are recreated, which caused the navigation loop.
  useEffect(() => {
    // Only run on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let urlListener: any;
    let stateListener: any;
    let backButtonListener: any;
    let mounted = true;

    // Track the deep-link-resolved path so we can block re-navigation
    let deepLinkResolvedPath: string | null = null;

    console.log("[Deep Link] Setting up listeners");

    /**
     * Navigate via deep link using `replace` instead of `push`.
     * This prevents the deep link from stacking duplicate history entries.
     * Also checks if we're already at the target path to avoid no-op navigations.
     */
    const deepLinkNavigate = (path: string) => {
      const currentPath =
        locationRef.current.pathname + locationRef.current.search;
      if (currentPath === path) {
        console.log("[Deep Link] Already at path, skipping:", path);
        return;
      }
      console.log("[Deep Link] Navigating to:", path);
      deepLinkResolvedPath = path;
      navigateRef.current(path, { replace: true });
    };

    // Shared handler for incoming URLs (both cold-launch and warm appUrlOpen)
    const handleIncomingUrl = async (incomingUrl: string) => {
      // Dedup: skip if we recently processed the exact same URL
      const now = Date.now();
      if (
        lastProcessedDeepLink &&
        lastProcessedDeepLink.url === incomingUrl &&
        now - lastProcessedDeepLink.timestamp < DEEP_LINK_DEDUP_WINDOW_MS
      ) {
        console.log("[Deep Link] Dedup: same URL processed recently, ignoring:", incomingUrl);
        return;
      }

      // If a deep link was already resolved and user hasn't navigated away, skip
      if (deepLinkResolvedPath) {
        const currentPath =
          locationRef.current.pathname + locationRef.current.search;
        if (currentPath === deepLinkResolvedPath) {
          console.log("[Deep Link] Already resolved, ignoring:", incomingUrl);
          return;
        }
        // User navigated away — clear the resolved path
        deepLinkResolvedPath = null;
      }

      // Record this URL as being processed
      lastProcessedDeepLink = { url: incomingUrl, timestamp: now };

      try {
        console.log("[Deep Link] Processing URL:", incomingUrl);

        // Handle OAuth callback URLs (contain access_token or code in hash/query)
        const url = new URL(incomingUrl);
        const hashParams = new URLSearchParams(url.hash.replace("#", ""));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");

        if (accessToken && refreshToken) {
          console.log(
            "[Deep Link] OAuth callback detected, setting session...",
          );
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            console.error("[Deep Link] OAuth session error:", error);
            toast.error("Sign in failed. Please try again.");
          } else {
            console.log("[Deep Link] OAuth session set successfully");
            deepLinkNavigate("/app");
          }
          return;
        }

        // Also handle ?code= flow (PKCE)
        const code = url.searchParams.get("code");
        if (code) {
          console.log("[Deep Link] OAuth PKCE code detected, exchanging...");
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.error("[Deep Link] OAuth code exchange error:", error);
            toast.error("Sign in failed. Please try again.");
          } else {
            console.log("[Deep Link] OAuth code exchange successful");
            deepLinkNavigate("/app");
          }
          return;
        }

        // Wait for auth to initialize if still loading
        if (authLoadingRef.current) {
          console.log("[Deep Link] Auth still loading, storing URL");
          setPendingUrlRef.current(incomingUrl);
          return;
        }

        // Use deep link service to handle the link.
        // Pass deepLinkNavigate so it uses replace instead of push.
        const currentUser = userRef.current;
        console.log(
          "[Deep Link] Handling deep link, authenticated:",
          !!currentUser?.id,
        );
        const handled = await deepLinkService.handleDeepLink(
          incomingUrl,
          currentUser?.id,
          deepLinkNavigate,
          {
            showErrors: true,
            trackAnalytics: true,
            fallbackPath: "/",
          },
        );
        console.log("[Deep Link] Result:", handled ? "handled" : "not handled");

        if (!handled && !currentUser) {
          console.log("[Deep Link] Auth required, storing pending URL");
          setPendingUrlRef.current(incomingUrl);
          toast.info("Sign in to view this content");
        } else if (!handled) {
          const validation = await deepLinkService.validateDeepLink(
            incomingUrl,
            currentUser?.id,
          );
          if (validation.errorMessage) {
            toast.error(validation.errorMessage);
          }
        }
      } catch (error) {
        console.error("[Deep Link] Error handling URL:", error);
        toast.error("Failed to open link");
      }
    };

    // Setup listeners (async in Capacitor 6+)
    const setupListeners = async () => {
      // Check if app was cold-launched via a deep link (e.g., OAuth callback).
      // getLaunchUrl() returns the same URL on every call, so we must only
      // process it once to avoid re-navigating on re-mounts.
      if (!launchUrlProcessed) {
        try {
          const launchUrl = await CapApp.getLaunchUrl();
          console.log(
            "[Deep Link] getLaunchUrl result:",
            launchUrl?.url || "none",
          );
          if (launchUrl?.url) {
            launchUrlProcessed = true;
            console.log(
              "[Deep Link] App cold-launched with URL:",
              launchUrl.url,
            );
            await handleIncomingUrl(launchUrl.url);
          }
        } catch (e) {
          console.error("[Deep Link] Failed to check launch URL:", e);
        }
      } else {
        console.log("[Deep Link] getLaunchUrl already processed, skipping");
      }

      // Listen for app URL open events (warm starts)
      urlListener = await CapApp.addListener(
        "appUrlOpen",
        async (event: URLOpenListenerEvent) => {
          console.log("[Deep Link] appUrlOpen:", event.url);
          await handleIncomingUrl(event.url);
        },
      );

      // Listen for app state changes
      stateListener = await CapApp.addListener(
        "appStateChange",
        ({ isActive }) => {
          if (isActive) {
            console.log("[App] App became active, refreshing saved items");
            // Dismiss any stale toasts so old errors don't re-appear on resume
            toast.dismiss();
            // Refresh saved items in case share extension added new items
            queryClientRef.current.invalidateQueries({
              queryKey: ["saved-items"],
            });

            // Auto-process items saved from share extension
            const currentUser = userRef.current;
            if (currentUser?.id) {
              analyzeUnprocessedImageItems(
                currentUser.id,
                queryClientRef.current,
              );
              extractUnprocessedUrlItems(
                currentUser.id,
                queryClientRef.current,
              );
            }
          } else {
            console.log("[App] App went to background");
          }
        },
      );

      // Android back button handling
      backButtonListener = await CapApp.addListener(
        "backButton",
        ({ canGoBack }) => {
          if (canGoBack) {
            window.history.back();
          } else {
            // On root page, minimize app instead of closing
            CapApp.minimizeApp();
          }
        },
      );

      console.log("[Deep Link] Listeners registered");

      // If already unmounted, cleanup immediately
      if (!mounted) {
        urlListener?.remove();
        stateListener?.remove();
        backButtonListener?.remove();
      }
    };

    setupListeners();

    // Cleanup only on unmount
    return () => {
      console.log("[Deep Link] Cleaning up listeners");
      mounted = false;
      urlListener?.remove();
      stateListener?.remove();
      backButtonListener?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Single-run: refs provide access to latest values

  // Process pending deep link after user logs in
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!user || !pendingUrl || authLoading) return;

    console.log("[Deep Link] Processing pending URL after login:", pendingUrl);

    let cancelled = false;

    const processPendingLink = async () => {
      const handled = await deepLinkService.handleDeepLink(
        pendingUrl,
        user.id,
        (path: string) => navigate(path, { replace: true }),
        {
          showErrors: true,
          trackAnalytics: true,
          fallbackPath: "/",
        },
      );

      // Don't proceed if component unmounted during processing
      if (cancelled) return;

      // Clear pending URL
      setPendingUrl(null);

      if (!handled) {
        const validation = await deepLinkService.validateDeepLink(
          pendingUrl,
          user.id,
        );
        if (validation.errorMessage) {
          toast.error(validation.errorMessage);
        }
      }
    };

    processPendingLink();

    return () => {
      cancelled = true;
    };
  }, [user, pendingUrl, authLoading, setPendingUrl, navigate]);
};
